import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the shape of a transcription metric
export interface TranscriptionMetric {
  id: string;
  transcriptionLength: number; // Keep for backward compatibility
  inputType: 'audio' | 'text';
  inputLength: number; // seconds for audio, characters for text
  createdAt: string;
}

// Define the shape of the savings settings
export interface SavingsSettings {
  hourlyRate: number;
  currency: string;
  minutesPerTranscription: number;
}

// Define the state shape
interface SavingsState {
  settings: SavingsSettings;
  transcriptionMetrics: TranscriptionMetric[];
}

// Default minutes per transcription
const DEFAULT_MINUTES_PER_TRANSCRIPTION = 20;

// Initial state
const initialState: SavingsState = {
  settings: {
    hourlyRate: 50,
    currency: 'USD',
    minutesPerTranscription: DEFAULT_MINUTES_PER_TRANSCRIPTION
  },
  transcriptionMetrics: []
};

// Create the slice
export const savingsSlice = createSlice({
  name: 'savings',
  initialState,
  reducers: {
    // Update settings
    updateSavingsSettings: (state, action: PayloadAction<Partial<SavingsSettings>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    
    // Set transcription metrics
    setTranscriptionMetrics: (state, action: PayloadAction<TranscriptionMetric[]>) => {
      state.transcriptionMetrics = action.payload;
    },
    
    // Add a new transcription metric
    addTranscriptionMetric: (state, action: PayloadAction<TranscriptionMetric>) => {
      state.transcriptionMetrics.push(action.payload);
    }
  },
});

// Export actions
export const { 
  updateSavingsSettings, 
  setTranscriptionMetrics,
  addTranscriptionMetric
} = savingsSlice.actions;

// Helper function to calculate time saved in seconds for a single metric
export const calculateTimeSavedForMetric = (metric: TranscriptionMetric): number => {
  if (metric.inputType === 'audio') {
    // For audio: recording time * 4
    return metric.inputLength * 4;
  } else {
    // For text: characters / 4 chars per second * 4 = characters (in seconds)
    return metric.inputLength;
  }
};

// Helper function to format time from seconds
export const formatTimeFromSeconds = (seconds: number): string => {
  if (seconds === 0) return '0m';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  let result = '';
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0 || hours === 0) result += `${minutes}m`;
  
  return result.trim();
};

// Helper function to calculate cost saving
export const calculateCostSaving = (seconds: number, hourlyRate: number): number => {
  const hours = seconds / 3600;
  return hours * hourlyRate;
};

// Calculate total time saved using new logic
export const calculateTotalTimeSaved = (metrics: TranscriptionMetric[]): number => {
  return metrics.reduce((total, metric) => {
    return total + calculateTimeSavedForMetric(metric);
  }, 0);
};

// Calculate today's time saved using new logic
export const calculateTodayTimeSaved = (metrics: TranscriptionMetric[]): number => {
  const today = new Date();
  
  // Create start and end of today in local timezone
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
  
  // Filter metrics for today using proper date comparison
  const todayMetrics = metrics.filter(metric => {
    const metricDate = new Date(metric.createdAt);
    return metricDate >= startOfToday && metricDate <= endOfToday;
  });
  
  return todayMetrics.reduce((total, metric) => {
    return total + calculateTimeSavedForMetric(metric);
  }, 0);
};

// Format time values in a user-friendly format with "mins" and "hrs"
export const formatTimeDisplayForNavbar = (seconds: number): string => {
  // Don't round - use exact values
  const minutes = seconds / 60;
  
  // If it's less than 60 minutes, display in minutes
  if (minutes < 60) {
    const displayMinutes = Math.floor(minutes * 10) / 10; // One decimal place without rounding up
    return displayMinutes < 1 ? '< 1 min' : `${displayMinutes} mins`;
  }
  
  // Convert to hours with decimal for display
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  
  // If there are no remaining minutes, just show hours
  if (remainingMinutes === 0) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  
  // Otherwise show hours and minutes
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes} mins`;
}

export default savingsSlice.reducer; 